import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, Target, Zap, Star, TrendingUp, Flame, Medal } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import BirdAvatar from './BirdAvatar';
import ShareButton from './ShareButton';
import AchievementBadge from './AchievementBadge';
import useGameStore from '../store/gameStore';
import { useToast } from '../hooks/use-toast';

const ProfileModal = ({ isOpen, onClose }) => {
  const {
    nickname,
    avatarId
  } = useGameStore();
  const { toast } = useToast();
  
  const [playerStats, setPlayerStats] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [playerRankings, setPlayerRankings] = React.useState(null);
  const [streak, setStreak] = React.useState({ current: 0, longest: 0 });
  const [achievements, setAchievements] = React.useState([]);
  const [achievementsUnlocked, setAchievementsUnlocked] = React.useState(0);
  
  // Fetch player stats from backend
  React.useEffect(() => {
    const fetchStats = async () => {
      if (!nickname || !isOpen) return;
      
      setIsLoading(true);
      try {
        const [statsRes, rankingsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/stats`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/rankings`)
        ]);
        
        if (statsRes.ok) {
          const data = await statsRes.json();
          if (data.success) {
            setPlayerStats({
              totalScore: data.player.total_score || 0,
              gamesPlayed: data.player.games_played || 0,
              correctGuesses: data.player.correct_guesses || 0,
              jailBirdBonusesWon: data.player.jailbird_bonuses_won || 0,
              gameHistory: data.game_history || []
            });
            setStreak({ current: data.current_streak || 0, longest: data.longest_streak || 0 });
            setAchievements(data.achievements || []);
            setAchievementsUnlocked(data.achievements_unlocked || 0);

            // One-time welcome toast for achievements earned in the past (backfill)
            const fresh = data.new_achievements || [];
            if (fresh.length > 0) {
              toast({
                title: `🎉 ${fresh.length} Achievement${fresh.length > 1 ? 's' : ''} Unlocked!`,
                description: fresh.length === 1
                  ? `You've earned "${fresh[0].name}". Check it out below!`
                  : `You've earned past achievements including "${fresh[0].name}". See them all below!`,
              });
            }
          }
        }
        
        if (rankingsRes.ok) {
          const rankingsData = await rankingsRes.json();
          if (rankingsData.success) {
            setPlayerRankings(rankingsData.rankings);
          }
        }
      } catch (error) {
        console.error('Error fetching player stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, [nickname, isOpen]);
  
  if (!playerStats && !isLoading) {
    return null;
  }
  
  const {
    totalScore = 0,
    gamesPlayed = 0,
    correctGuesses = 0,
    jailBirdBonusesWon = 0,
    gameHistory = []
  } = playerStats || {};

  // Calculate stats
  const totalQuestions = gamesPlayed * 15; // 15 questions per game
  const accuracy = totalQuestions > 0 ? Math.round((correctGuesses / totalQuestions) * 100) : 0;
  const averageScore = gamesPlayed > 0 ? Math.round(totalScore / gamesPlayed) : 0;

  // Best game
  const bestGame = gameHistory.length > 0 ? gameHistory.reduce((best, game) => 
    game.score > best.score ? game : best
  , gameHistory[0]) : null;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <Card className="p-6 sm:p-8 bg-gradient-card border-border">
              {isLoading ? (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading stats...</p>
                </div>
              ) : (
                <>
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Player Info */}
              <div className="flex flex-col items-center mb-6 sm:mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                  className="mb-4"
                >
                  <BirdAvatar
                    avatarId={avatarId}
                    size={120}
                    animate={true}
                  />
                </motion.div>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gradient-primary mb-2">
                  {nickname}
                </h2>
                <p className="text-muted-foreground">Player Statistics</p>
              </div>

              {/* Overall Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="p-3 sm:p-4 rounded-lg bg-primary/10 border border-primary/20 text-center">
                  <Trophy className="w-5 h-5 text-primary mx-auto mb-1" />
                  <div className="text-xl sm:text-2xl font-heading font-bold text-gradient-primary">
                    {totalScore.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Score</div>
                </div>
                
                <div className="p-3 sm:p-4 rounded-lg bg-accent/10 border border-accent/20 text-center">
                  <Target className="w-5 h-5 text-accent mx-auto mb-1" />
                  <div className="text-xl sm:text-2xl font-heading font-bold text-gradient-accent">
                    {accuracy}%
                  </div>
                  <div className="text-xs text-muted-foreground">Accuracy</div>
                </div>
                
                <div className="p-3 sm:p-4 rounded-lg bg-success/10 border border-success/20 text-center">
                  <Zap className="w-5 h-5 text-success mx-auto mb-1" />
                  <div className="text-xl sm:text-2xl font-heading font-bold text-success">
                    {gamesPlayed}
                  </div>
                  <div className="text-xs text-muted-foreground">Games</div>
                </div>
                
                <div className="p-3 sm:p-4 rounded-lg bg-gold/10 border border-gold/20 text-center">
                  <Star className="w-5 h-5 text-gold mx-auto mb-1" />
                  <div className="text-xl sm:text-2xl font-heading font-bold text-gold">
                    {jailBirdBonusesWon}
                  </div>
                  <div className="text-xs text-muted-foreground">Bonuses</div>
                </div>
              </div>

              {/* More Stats */}
              <div className="space-y-3">
                <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  More Stats
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-muted/20 border border-border">
                    <div className="text-sm text-muted-foreground mb-1">Avg Score/Game</div>
                    <div className="text-xl font-bold text-gradient-primary">{averageScore}</div>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-muted/20 border border-border">
                    <div className="text-sm text-muted-foreground mb-1">Total Correct</div>
                    <div className="text-xl font-bold text-gradient-accent">{correctGuesses}</div>
                  </div>
                  
                  {bestGame && (
                    <>
                      <div className="p-3 rounded-lg bg-muted/20 border border-border">
                        <div className="text-sm text-muted-foreground mb-1">Best Score</div>
                        <div className="text-xl font-bold text-gold">{bestGame.score}</div>
                      </div>
                      
                      <div className="p-3 rounded-lg bg-muted/20 border border-border">
                        <div className="text-sm text-muted-foreground mb-1">Best Accuracy</div>
                        <div className="text-xl font-bold text-success">{bestGame.accuracy}%</div>
                      </div>
                    </>
                  )}
                  
                  <div className="p-3 rounded-lg bg-muted/20 border border-border">
                    <div className="text-sm text-muted-foreground mb-1">Questions Seen</div>
                    <div className="text-xl font-bold">{totalQuestions}</div>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-muted/20 border border-border">
                    <div className="text-sm text-muted-foreground mb-1">Win Rate</div>
                    <div className="text-xl font-bold">{accuracy}%</div>
                  </div>
                </div>
              </div>

              {/* Global Rankings */}
              {playerRankings && (
                <div className="space-y-3">
                  <h3 className="text-lg font-heading font-semibold">
                    Global Rankings
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="text-sm text-muted-foreground mb-1">Points Rank</div>
                      <div className="text-2xl font-bold text-primary">
                        #{playerRankings.points_rank}
                      </div>
                      <div className="text-xs text-muted-foreground">of {playerRankings.total_players} players</div>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="text-sm text-muted-foreground mb-1">Games Rank</div>
                      <div className="text-2xl font-bold text-primary">
                        #{playerRankings.games_rank}
                      </div>
                      <div className="text-xs text-muted-foreground">of {playerRankings.total_players} players</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Daily Streak */}
              <div className="space-y-3 mt-6" data-testid="profile-streak-section">
                <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-400" />
                  Daily Streak
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-lg bg-gradient-to-b from-orange-500/15 to-orange-900/5 border border-orange-500/30 text-center">
                    <div className="text-3xl font-heading font-bold text-orange-400" data-testid="current-streak-value">{streak.current}</div>
                    <div className="text-xs text-muted-foreground mt-1">Current Streak</div>
                  </div>
                  <div className="p-4 rounded-lg bg-gradient-to-b from-amber-500/15 to-amber-900/5 border border-amber-500/30 text-center">
                    <div className="text-3xl font-heading font-bold text-amber-400">{streak.longest}</div>
                    <div className="text-xs text-muted-foreground mt-1">Longest Streak</div>
                  </div>
                </div>
              </div>

              {/* Achievements */}
              <div className="space-y-3 mt-6" data-testid="profile-achievements-section">
                <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <Medal className="w-5 h-5 text-gold" />
                  Achievements
                  <span className="text-sm font-normal text-muted-foreground">
                    ({achievementsUnlocked}/{achievements.length})
                  </span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {achievements.map((ach) => (
                    <AchievementBadge key={ach.id} achievement={ach} unlocked={ach.unlocked} />
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <ShareButton
                  nickname={nickname}
                  avatarId={avatarId}
                  totalScore={totalScore}
                  gamesPlayed={gamesPlayed}
                  accuracy={accuracy || 0}
                  variant="outline"
                  size="lg"
                  showLabel={true}
                />
                <Button
                  onClick={onClose}
                  className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
                  size="lg"
                >
                  Close
                </Button>
              </div>
              </>
              )}
            </Card>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProfileModal;
